import { getBike, getModels, MY_BIKE_EVENT } from '@theme/my-bike';

/**
 * Fitment UI driven by the "My bike" selection (assets/my-bike.js).
 *
 * The list of model collections is the header setting "Bike models", exposed once per page as
 * #my-bike-models (snippets/my-bike-selector.liquid). Products carry their collection IDs in
 * data-collection-ids; only collections that are in the model list count as fitment.
 *
 *  <fit-badge>   product page badge   – states: fits | not-listed | check | universal
 *  <fit-tag>     product card tag     – states: fits | model | universal | (none)
 *  <model-compat> compatibility table – hides non-model collections, uses short model names
 *
 * All copy is rendered (translated) by Liquid; JS only switches data-state and fills model names.
 */

/** @param {HTMLElement} element */
function collectionIds(element) {
  return (element.dataset.collectionIds || '')
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
}

/** @param {string[]} ids */
function matchingModels(ids) {
  const models = getModels();
  return models.filter((model) => ids.includes(String(model.id)));
}

/**
 * @param {ParentNode} root
 * @param {string} selector
 * @param {string} text
 */
function fill(root, selector, text) {
  for (const node of root.querySelectorAll(selector)) node.textContent = text;
}

class FitElement extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    document.addEventListener(MY_BIKE_EVENT, () => this.update(), { signal: this.#controller.signal });
    this.update();
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  update() {}
}

class FitBadge extends FitElement {
  update() {
    const bike = getBike();
    const ids = collectionIds(this);
    const models = matchingModels(ids);
    const universal = this.dataset.universal === 'true';
    let state = 'check';

    if (bike && ids.includes(String(bike.id))) {
      state = 'fits';
    } else if (universal) {
      state = 'universal';
    } else if (bike && models.length) {
      state = 'not-listed';
    }

    if (bike) fill(this, '[data-fit-model]', bike.title);
    this.dataset.state = state;
    this.dataset.bike = bike ? 'true' : 'false';
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', (event) => {
      const target = /** @type {HTMLElement} */ (event.target);
      if (!target.closest('[data-fit-select-bike]')) return;
      const toggles = /** @type {HTMLElement[]} */ (Array.from(document.querySelectorAll('[data-my-bike-toggle]')));
      const visible = toggles.find((toggle) => toggle.offsetParent !== null);
      if (visible) {
        visible.scrollIntoView({ block: 'nearest' });
        // After this click finishes bubbling, so the popover's outside-click handler doesn't close it.
        setTimeout(() => visible.click(), 0);
      }
    });
  }
}

class FitTag extends FitElement {
  update() {
    const bike = getBike();
    const ids = collectionIds(this);
    const models = matchingModels(ids);
    let state = '';

    if (bike && ids.includes(String(bike.id))) {
      state = 'fits';
      fill(this, '[data-fit-model]', bike.title);
    } else if (this.dataset.universal === 'true') {
      state = 'universal';
    } else if (models.length) {
      state = 'model';
      const first = models[0]?.title || '';
      fill(this, '[data-fit-models]', models.length > 1 ? `${first} +${models.length - 1}` : first);
    }

    this.dataset.state = state;
    this.dataset.bike = bike ? 'true' : 'false';
    this.hidden = state === '';
  }
}

class ModelCompat extends FitElement {
  update() {
    const bike = getBike();
    const models = getModels();
    const byId = new Map(models.map((model) => [String(model.id), model]));
    let visible = 0;

    for (const row of /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[data-collection-id]'))) {
      const id = row.dataset.collectionId || '';
      const model = byId.get(id);
      const show = models.length ? Boolean(model) : row.dataset.likelyModel === 'true';
      row.hidden = !show;
      if (!show) continue;
      visible += 1;
      if (model) fill(row, '[data-model-name]', model.title);
      row.classList.toggle('is-current', Boolean(bike && String(bike.id) === id));
    }

    this.dataset.count = String(visible);
  }
}

if (!customElements.get('fit-badge')) customElements.define('fit-badge', FitBadge);
if (!customElements.get('fit-tag')) customElements.define('fit-tag', FitTag);
if (!customElements.get('model-compat')) customElements.define('model-compat', ModelCompat);